import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface DataAccessRange {
  weeksPast: number;
  weeksFuture: number;
  minDate: Date;
  maxDate: Date;
}

interface PermissionContextType {
  permissions: string[];
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  refreshPermissions: () => Promise<void>;
  dataAccessRange: DataAccessRange | null;
  isDateInRange: (date: Date | string) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components -- context must be exported for usePermissions hook
export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataAccessRange, setDataAccessRange] = useState<DataAccessRange | null>(null);

  const fetchPermissions = useCallback(async (role: string) => {
    const { data, error } = await supabase
      .from('role_permissions')
      .select('permission')
      .eq('role', role);

    if (!error && data) {
      setPermissions(data.map(p => p.permission));
    } else {
      setPermissions([]);
    }
  }, []);

  // Helper to get Monday of a given week
  const getMondayOfWeek = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    // getDay() returns 0 for Sunday, 1 for Monday, etc.
    // To get Monday: subtract (day - 1), but handle Sunday (day=0) as day=7
    const diff = day === 0 ? 6 : day - 1;
    d.setDate(d.getDate() - diff);
    return d;
  };

  const fetchDataAccessRange = useCallback(async (role: string) => {
    const { data, error } = await supabase
      .from('role_data_access')
      .select('weeks_past, weeks_future')
      .eq('role', role)
      .single();

    if (!error && data) {
      // Calculate full week range (Monday to Sunday)
      const currentWeekMonday = getMondayOfWeek(new Date());

      // min_date = Monday of (current week - weeks_past weeks)
      const minDate = new Date(currentWeekMonday);
      minDate.setDate(minDate.getDate() - (data.weeks_past * 7));

      // max_date = Sunday of (current week + weeks_future weeks)
      const maxDate = new Date(currentWeekMonday);
      maxDate.setDate(maxDate.getDate() + (data.weeks_future * 7) + 6); // +6 for Sunday

      setDataAccessRange({
        weeksPast: data.weeks_past,
        weeksFuture: data.weeks_future,
        minDate,
        maxDate,
      });
    } else {
      // Default to viewer settings (1 week each direction, full weeks)
      const currentWeekMonday = getMondayOfWeek(new Date());

      const minDate = new Date(currentWeekMonday);
      minDate.setDate(minDate.getDate() - 7); // 1 week back

      const maxDate = new Date(currentWeekMonday);
      maxDate.setDate(maxDate.getDate() + 7 + 6); // 1 week forward + Sunday

      setDataAccessRange({
        weeksPast: 1,
        weeksFuture: 1,
        minDate,
        maxDate,
      });
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      if (profile?.role) {
        setLoading(true);
        await Promise.all([
          fetchPermissions(profile.role),
          fetchDataAccessRange(profile.role),
        ]);
        setLoading(false);
      } else {
        setPermissions([]);
        setDataAccessRange(null);
        setLoading(false);
      }
    };
    void loadAll();
  }, [profile?.role, fetchPermissions, fetchDataAccessRange]);

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const isDateInRange = useCallback((date: Date | string): boolean => {
    if (!dataAccessRange) return true; // Allow if not loaded yet
    let checkDate: Date;
    if (typeof date === 'string') {
      // Parse string as local date (YYYY-MM-DD) to avoid UTC interpretation
      // new Date("2026-01-29") parses as UTC midnight, causing timezone mismatch
      const [year, month, day] = date.split('-').map(Number);
      checkDate = new Date(year, month - 1, day);
    } else {
      checkDate = new Date(date);
    }
    checkDate.setHours(0, 0, 0, 0);
    return checkDate >= dataAccessRange.minDate && checkDate <= dataAccessRange.maxDate;
  }, [dataAccessRange]);

  const refreshPermissions = async () => {
    if (profile?.role) {
      setLoading(true);
      await Promise.all([
        fetchPermissions(profile.role),
        fetchDataAccessRange(profile.role),
      ]);
      setLoading(false);
    }
  };

  return (
    <PermissionContext.Provider value={{
      permissions,
      loading,
      hasPermission,
      refreshPermissions,
      dataAccessRange,
      isDateInRange,
    }}>
      {children}
    </PermissionContext.Provider>
  );
}
